import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import PageTitle from '../../components/Common/PageTitle';

const PrivacyPolicy = () => {
  const navigate = useNavigate();

  useEffect(() => {
    // Scroll to top when component mounts
    window.scrollTo(0, 0);
  }, []);

  return (
    <div>
      <div className="p-4">
        <div className="bg-white rounded-xl p-3 font-title">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center text-blue-600 hover:text-blue-800 mb-4"
          >
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
            Back
          </button>
          
          <PageTitle title="Privacy Policy" />
          <p className="text-gray-600 mt-2 mb-6">
            Last updated: {new Date().toLocaleDateString()}
          </p>

          <div className="prose prose-lg max-w-none">
            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Introduction</h2>
              <p className="text-gray-700 mb-4">
                Welcome to LogiSlip ("we," "our," or "us"). We are committed to protecting your privacy and personal data. 
                This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use 
                our invoice management application and cloud synchronization services.
              </p>
              <p className="text-gray-700 mb-4">
                LogiSlip is a comprehensive invoice management solution that helps businesses create, manage, and track 
                invoices, clients, and products. Our services include local data storage, cloud synchronization, 
                and Google Drive integration for backup and sharing purposes.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Information We Collect</h2>
              
              <h3 className="text-xl font-semibold text-gray-800 mb-3">Personal Information</h3>
              <ul className="list-disc list-inside text-gray-700 mb-4 space-y-2">
                <li>Name and contact information (email address, phone number)</li>
                <li>Business information (company name, address, tax identification numbers)</li>
                <li>Payment information (UPI details, transaction IDs, billing information)</li>
                <li>Account credentials and preferences</li>
                <li>Phone verification and SMS/WhatsApp communication preferences</li>
              </ul>

              <h3 className="text-xl font-semibold text-gray-800 mb-3">Business Data</h3>
              <ul className="list-disc list-inside text-gray-700 mb-4 space-y-2">
                <li>Invoice data (client information, products, services, amounts)</li>
                <li>Client and customer information</li>
                <li>Product and service catalogs</li>
                <li>Financial records and transaction history</li>
              </ul>

              <h3 className="text-xl font-semibold text-gray-800 mb-3">Technical Information</h3>
              <ul className="list-disc list-inside text-gray-700 mb-4 space-y-2">
                <li>Device information (browser type, operating system)</li>
                <li>Usage data (features used, time spent in application)</li>
                <li>IP address and general location information</li>
                <li>Cookies and similar tracking technologies</li>
                <li>Payment transaction data and subscription information</li>
                <li>Phone verification status and communication logs</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">How We Use Your Information</h2>
              <ul className="list-disc list-inside text-gray-700 mb-4 space-y-2">
                <li>Provide and maintain our invoice management services</li>
                <li>Process your transactions and manage your account</li>
                <li>Process payments through Razorpay and manage subscriptions</li>
                <li>Send you important updates and notifications via email, SMS, or WhatsApp</li>
                <li>Verify your phone number for account security</li>
                <li>Improve our application and develop new features</li>
                <li>Provide customer support and technical assistance</li>
                <li>Comply with legal obligations and prevent fraud</li>
                <li>Send account verification codes and security notifications</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Data Storage and Security</h2>
              <p className="text-gray-700 mb-4">
                We implement appropriate technical and organizational measures to protect your data:
              </p>
              <ul className="list-disc list-inside text-gray-700 mb-4 space-y-2">
                <li>Encryption of data in transit and at rest</li>
                <li>Regular security assessments and updates</li>
                <li>Access controls and authentication measures</li>
                <li>Secure cloud storage with reputable providers</li>
                <li>Regular backups and disaster recovery procedures</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Data Sharing and Disclosure</h2>
              <p className="text-gray-700 mb-4">
                We do not sell, trade, or otherwise transfer your personal information to third parties except:
              </p>
              <ul className="list-disc list-inside text-gray-700 mb-4 space-y-2">
                <li>With your explicit consent</li>
                <li>To Google Drive when you use cloud synchronization features (using your own Google account)</li>
                <li>To Gmail when you send invoices via email (using your own Gmail account)</li>
                <li>To Razorpay for payment processing when you subscribe to premium plans</li>
                <li>To Twilio for SMS and WhatsApp communications for phone verification</li>
                <li>When required by law or legal process</li>
                <li>To protect our rights, property, or safety</li>
              </ul>
              <p className="text-gray-700 mt-4">
                <strong>Third-Party Service Providers:</strong>
              </p>
              <ul className="list-disc list-inside text-gray-700 mb-4 space-y-2">
                <li><strong>Google Services Integration:</strong> When you use our Google Drive sync or Gmail features, 
                your data is transmitted directly to your own Google account. We do not store or access your 
                Google account credentials beyond the session tokens required for these features.</li>
                <li><strong>Razorpay:</strong> For payment processing, your payment information is securely transmitted 
                to Razorpay. We only store transaction references and payment status.</li>
                <li><strong>Twilio:</strong> For phone verification, your phone number and verification codes are 
                transmitted to Twilio for SMS and WhatsApp delivery. We do not store SMS content.</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Your Rights and Choices</h2>
              <p className="text-gray-700 mb-4">
                You have the following rights regarding your personal data:
              </p>
              <ul className="list-disc list-inside text-gray-700 mb-4 space-y-2">
                <li><strong>Access:</strong> Request access to your personal data</li>
                <li><strong>Rectification:</strong> Request correction of inaccurate data</li>
                <li><strong>Erasure:</strong> Request deletion of your data</li>
                <li><strong>Portability:</strong> Request transfer of your data</li>
                <li><strong>Objection:</strong> Object to processing of your data</li>
                <li><strong>Restriction:</strong> Request restriction of processing</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Cookies and Tracking</h2>
              <p className="text-gray-700 mb-4">
                We use cookies and similar technologies to enhance your experience:
              </p>
              <ul className="list-disc list-inside text-gray-700 mb-4 space-y-2">
                <li>Essential cookies for application functionality</li>
                <li>Analytics cookies to understand usage patterns</li>
                <li>Preference cookies to remember your settings</li>
                <li>Authentication cookies for secure access</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Data Retention</h2>
              <p className="text-gray-700 mb-4">
                We retain your data only as long as necessary for the purposes outlined in this policy:
              </p>
              <ul className="list-disc list-inside text-gray-700 mb-4 space-y-2">
                <li>Account data: Until account deletion or as required by law</li>
                <li>Invoice data: For tax and legal compliance periods (typically 7 years)</li>
                <li>Payment data: For financial compliance and fraud prevention (up to 7 years)</li>
                <li>Phone verification data: Until account deletion or number change</li>
                <li>Usage data: Typically 24 months for analytics purposes</li>
                <li>Support data: Until resolved and for quality assurance (typically 2 years)</li>
                <li>SMS/WhatsApp logs: 90 days for delivery confirmation</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Third-Party Services</h2>
              <p className="text-gray-700 mb-4">
                Our Service integrates with the following third-party providers:
              </p>
              <ul className="list-disc list-inside text-gray-700 mb-4 space-y-2">
                <li><strong>Google Services:</strong> Google Drive API for cloud synchronization and Gmail API for email sharing</li>
                <li><strong>Razorpay:</strong> Payment processing for subscription management</li>
                <li><strong>Twilio:</strong> SMS and WhatsApp services for phone verification</li>
                <li><strong>Supabase:</strong> Database and authentication services</li>
              </ul>
              <p className="text-gray-700 mb-4">
                Each service has its own privacy policy and terms of service. We recommend reviewing their policies:
              </p>
              <ul className="list-disc list-inside text-gray-700 mb-4 space-y-2">
                <li>Google Privacy Policy: https://policies.google.com/privacy</li>
                <li>Razorpay Privacy Policy: https://razorpay.com/privacy/</li>
                <li>Twilio Privacy Policy: https://www.twilio.com/legal/privacy</li>
                <li>Supabase Privacy Policy: https://supabase.com/privacy</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">International Data Transfers</h2>
              <p className="text-gray-700 mb-4">
                Your data may be transferred to and processed in countries other than your own. 
                We ensure appropriate safeguards are in place to protect your data during such transfers.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Children's Privacy</h2>
              <p className="text-gray-700 mb-4">
                Our service is not intended for children under 18. We do not knowingly collect 
                personal information from children under 18. If you become aware that a child 
                has provided us with personal information, please contact us.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Changes to This Policy</h2>
              <p className="text-gray-700 mb-4">
                We may update this Privacy Policy from time to time. We will notify you of any 
                material changes by posting the new policy on this page and updating the "last updated" date.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Governing Law</h2>
              <p className="text-gray-700 mb-4">
                This Privacy Policy is governed by the laws of India. Any disputes relating to 
                this policy will be subject to the exclusive jurisdiction of the courts in Bangalore, India.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Contact Us</h2>
              <p className="text-gray-700 mb-4">
                If you have questions about this Privacy Policy or our data practices, please contact us:
              </p>
              <div className="bg-gray-50 p-4 rounded-lg">
                <p className="text-gray-700 mb-2">
                  <strong>Email:</strong> naveenrayan0@gmail.com
                </p>
                <p className="text-gray-700 mb-2">
                  <strong>Address:</strong> #14, 2nd main, Indiragandhi street, Bangalore 560016
                </p>
                <p className="text-gray-700">
                  <strong>Phone:</strong> +91 9535354685
                </p>
              </div>
            </section>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy; 